import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cleanDomain } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";

/**
 * The two places a business is besides its social accounts: its own website, and its Google listing
 * (2026-09-20, the owner: *"we should display that we could have them… maybe we could add these in the business
 * page"*).
 *
 * It sits above the channels, in the same list and the same row, so one page answers *where is this business, and
 * what is Joogoos plugged into* — every place it could be, whether or not it is set up, and what each one needs.
 * That is the shape `ChannelsPanel` already uses for the six platforms; these two were the ones missing from it.
 *
 * **The website address is typed here and nowhere else**
 * ([ADR 0039](../../../../docs/adr/0039-a-business-has-one-website-address-and-it-is-typed-on-the-business-page.md)).
 * It used to be inside Analytics, which meant a tab of numbers carrying a form, and the one page about connections
 * not mentioning the one connection that works. SEO then asked for the same address a second time, in its own tab,
 * and kept its own copy — so a business could disagree with itself about where its own website is. Both tabs now
 * show what this row holds and send anybody who wants to change it here.
 */
export function Connections() {
  const site = useQuery({ queryKey: ["site", 30, null, null], queryFn: () => api.site(30) });
  // The same key the Business page reads, so this costs no second request.
  const profile = useQuery({ queryKey: ["profile"], queryFn: () => api.profile() });

  return (
    <div className="flex flex-col gap-2">
      <Website />
      <Row
        icon="shop"
        label="Google Business"
        dot={profile.data?.googleListing ? "good" : "idle"}
        // Not "found" and not "not found" until it is known which: a row that says a business is not on Google
        // while it is still reading is a row that has told the owner something untrue, however briefly.
        said={!profile.data ? "…" : profile.data.googleListing ? "found — Joogoos reads it when it looks your business up" : "not found yet"}
      >
        {profile.data && !profile.data.googleListing ? (
          // No button, because there is nothing to press: the listing is found by the lookup, not connected by hand.
          <p className="text-[12.5px] text-muted leading-relaxed">
            Joogoos looks for your listing when it reads your business. If you have one and it was not found, put the
            name on the listing and your town in Where to find you, and it looks again. Online only? Nothing to do.
          </p>
        ) : null}
      </Row>
      {site.isError || profile.isError ? <p className="text-xs text-bad">Some of this could not be read just now.</p> : null}
    </div>
  );
}

/** The website: not set up, set up and waiting, or counting — with the one thing to do in each. */
function Website() {
  const q = useQuery({
    queryKey: ["site", 30, null, null],
    queryFn: () => api.site(30),
    // While a line is waiting to be pasted, the row should change by itself when the first visit lands.
    refetchInterval: (query) => (query.state.data && query.state.data.domain && !query.state.data.connected ? 15_000 : false),
  });
  const s = q.data;
  const [changing, setChanging] = useState(false);
  const [copied, setCopied] = useState(false);
  // Nothing is offered before the answer arrives. The box used to mount while the query was still out, which fixed
  // its contents to empty for ever — `useState` keeps what it was first given, and the address Joogoos already
  // knew arrived a moment too late to be in it.
  const setting = !!s && (!s.domain || changing);

  return (
    <Row
      icon="analytics"
      label="Your website"
      dot={s?.connected ? "good" : s?.domain ? "waiting" : "idle"}
      said={
        !s ? "…"
          : s.connected && s.installedAt ? `counting ${s.domain} since ${day(s.installedAt)}`
          : s.domain ? `${s.domain} — waiting for the first visit`
          : "not set up"
      }
      action={s?.domain && !changing
        ? <button onClick={() => setChanging(true)} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium">Change</button>
        : undefined}
    >
      {setting ? <Address current={s?.domain ?? null} known={s?.known ?? null} onDone={() => setChanging(false)} onCancel={s?.domain ? () => setChanging(false) : undefined} /> : null}

      {/* The line to paste stays on screen until the first visit proves it is in. */}
      {!setting && s?.domain && !s.connected && s.snippet ? (
        <>
          <p className="text-[12.5px] text-muted leading-relaxed">
            Paste this just before the <code>&lt;/head&gt;</code> of your website — in a site builder it is usually a
            box called "custom code" or "header code". Nothing to press afterwards; this row changes by itself.
          </p>
          <div className="flex items-start gap-2">
            <pre className="flex-1 min-w-0 overflow-x-auto bg-page border border-line rounded-lg px-3 py-2 text-[11.5px]">{s.snippet}</pre>
            <button
              onClick={() => { void navigator.clipboard.writeText(s.snippet ?? ""); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="shrink-0 flex items-center gap-1.5 bg-ink text-white rounded-lg px-3 py-1.5 text-xs font-semibold"
            >
              <Icon name={copied ? "check" : "copy"} size={12} /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </>
      ) : null}

      {!setting && s?.connected ? (
        <>
          <p className="text-[12.5px] text-muted leading-relaxed">
            No cookies, nothing that follows anybody between websites, and nothing for your visitors to agree to. What
            it counts is on the Analytics tab, and the articles Joogoos writes are published under this address.
          </p>
          <Marking />
        </>
      ) : null}
    </Row>
  );
}

/**
 * How somebody getting in touch is counted, said where the marketing review's *Fix it* sends the owner.
 *
 * The review says "nobody was seen getting in touch" when the counter has counted visits and never an enquiry
 * (`enquiries_none_seen`), and its button opens this step. Until the first pilot round (2026-10-05) nothing here said
 * how a form or a button is marked, so the owner was sent to a page that could not tell them what to do. What is found
 * with nothing marked comes first — a phone, email or WhatsApp link — because the natural mistake is to go and mark
 * those; then the one attribute, on a button and on a form, and the rule a name has to keep, which is the counter's
 * own (`SITE_GOAL_NAME`, 35 §the event contract (a)): a name that breaks it is dropped without a word. Only once the
 * site is counted, since before that there is nothing to count a mark with.
 */
function Marking() {
  return (
    <section aria-label="What counts as getting in touch" className="rounded-lg border border-line bg-page px-3 py-2.5 flex flex-col gap-1.5 text-[12.5px] leading-relaxed">
      <span className="font-semibold">What counts as getting in touch</span>
      <p className="text-muted">
        Presses on your phone number, your email address and a WhatsApp link are counted by themselves: there is
        nothing to mark. A form or any other button is counted once it carries a short name of your own, such
        as <code>data-joogoos-goal="call_request"</code> on the button, or on the form itself:
      </p>
      <pre className="overflow-x-auto bg-panel border border-line rounded-lg px-3 py-2 text-[11.5px]">{'<form data-joogoos-goal="call_request">'}</pre>
      <p className="text-[11.5px] text-muted">
        A button counts when it is pressed and a form when it is sent. The name is small letters, digits, dashes or
        underscores, starting with a letter; only the name is sent, never what anybody typed. It shows on Analytics
        under who got in touch.
      </p>
    </section>
  );
}

/**
 * The business's one website address. Offered from the Business page's own reading of it wherever Joogoos already
 * knows it, and typed once: it is what visits are counted for *and* what articles are published under (ADR 0039).
 */
function Address({ current, known, onDone, onCancel }: {
  current: string | null; known: string | null; onDone: () => void; onCancel?: (() => void) | undefined;
}) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState(current ?? known ?? "");
  /**
   * Checked here as well as at the API, because a round trip is a slow way to be told you typed a word — and
   * because `https` is a word. It was accepted once, through the address field the SEO tab used to carry, and
   * stored as `https://https`; everything the product built from it was built on a website that does not exist.
   */
  const [wrong, setWrong] = useState("");
  const connect = useMutation({
    mutationFn: (domain: string) => api.connectSite(domain),
    // The address is the business's one website (ADR 0040), so SEO and Your website read it too: the SEO step went
    // on saying "needs your website address first" until a reload (live, 2026-09-29).
    onSuccess: () => {
      for (const key of [["site"], ["seo"], ["website"]]) void qc.invalidateQueries({ queryKey: key });
      onDone();
    },
  });
  const submit = () => {
    const domain = cleanDomain(typed);
    if (!domain) return setWrong("That does not look like a website address — try bakery.co.uk");
    // What was typed is replaced by what will be saved, so nobody has to wonder which of the two it kept.
    setWrong("");
    setTyped(domain);
    connect.mutate(domain);
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col gap-1.5">
      <p className="text-[12.5px] text-muted leading-relaxed">
        Your one website address. One line in it counts how many people came and what they came for, so the next
        post can be about what they are already interested in, and it is where the articles on the SEO tab are
        published. No cookies, and nothing for your visitors to agree to.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={typed}
          onChange={(e) => { setTyped(e.target.value); setWrong(""); }}
          placeholder="bakery.co.uk"
          aria-label="Your website address"
          aria-invalid={wrong ? true : undefined}
          className={`flex-1 min-w-[180px] bg-page border rounded-lg px-3 py-1.5 text-[13px] ${wrong ? "border-coral" : "border-line"}`}
        />
        <button type="submit" disabled={!typed.trim() || connect.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
          {connect.isPending ? "…" : current ? "Change it" : "Count it"}
        </button>
        {onCancel ? <button type="button" onClick={onCancel} className="text-xs text-muted px-1.5">Cancel</button> : null}
      </div>
      {current ? <p className="text-[11.5px] text-muted">If this is a different website, counting starts again from zero and articles are published under the new address.</p>
        : known ? <p className="text-[11.5px] text-muted">Read from your business page when Joogoos looked you up.</p>
        : null}
      {wrong ? <p className="text-xs text-coral">{wrong}</p> : null}
      {connect.error ? <p className="text-xs text-bad">{String(connect.error)}</p> : null}
    </form>
  );
}

/** One place, drawn the way `ChannelsPanel` draws a platform, so the three read as one list. */
function Row({ icon, label, dot, said, action, children }: {
  icon: "analytics" | "shop";
  label: string;
  dot: "good" | "waiting" | "idle";
  said: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-6 h-6 rounded-md bg-page border border-line grid place-items-center shrink-0"><Icon name={icon} size={13} /></span>
        <span className="text-sm font-semibold">{label}</span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className={`w-2 h-2 rounded-full ${dot === "good" ? "bg-good" : dot === "waiting" ? "bg-coral animate-pulse" : "bg-line-2"}`} />
          {said}
        </span>
        {action ? <span className="ml-auto flex items-center gap-2">{action}</span> : null}
      </div>
      {children}
    </div>
  );
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
